import { Box, Grid, Typography} from '@mui/material'
import React, { useEffect, useState } from 'react'
import {  Legend, Tooltip, CartesianGrid, XAxis, YAxis, Area, ResponsiveContainer, AreaChart, Label } from "recharts";
import { Call, Error, Timelapse, Speed } from '@mui/icons-material';
import { useParams } from "react-router-dom";
import { ChartContainer } from './Chart';
import { formatDate, formatDuration } from './Format';
import api from '../api';
import { downloadAIToHumanTransferCSV, downloadAvgDurationByIssueTypeCSV, downloadCallAnalysisReport, downloadFirstCallResolutionCSV, downloadPeakHourTrafficCSV,downloadWPMMetricsCSV } from './download';
import MetricCard from './Assistant/MetricCard';
import CallAnalysisGrid from './AssistantOverviewCharts/CallAnalysisGrid';
import FirstCallResolutionGrid from './AssistantOverviewCharts/FirstCallResolution';
import AIToHumanHandoverRateGrid from './AssistantOverviewCharts/AItoHumanHandoverGrid';
import AvgCallMinutesBarChart from './AssistantOverviewCharts/AverageCallingMinutes';
import Loader from './Loader';


const AssistantOverview = React.memo(({ filter }) => {
    const [displayData, setDisplayData] = useState({});
    const [loading, setLoading] = useState(false);
    const token = JSON.parse(localStorage.getItem("token"))?.token;
    const { assistantId } = useParams();
    const dataPoints = displayData?.wpmAnalysis?.dataPoints;
    const hasWpmData = Array.isArray(dataPoints) && dataPoints.length > 0;

    useEffect(() => {
        const getFilteredData = async () => {
            setLoading(true);
            try {
                const res = await api.get(`/call-logs/assistant-metrics/${assistantId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { filter },
                });
                setDisplayData(res.data);
            } catch (error) {
                console.error("Error fetching filtered data:", error);
                setDisplayData({});
            } finally {
                setLoading(false);
            }
        };

        if (token && assistantId) {
            getFilteredData();
        }
    }, [filter, assistantId, token]);// Ensure all dependencies are included
    

    if (loading) {
        return <Loader />;
    }

    const isIncrease = (percentageChange) => {
        return percentageChange && parseFloat(percentageChange) >= 0;
    };
  
    const formatYAxis = (value) => {
      if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
      if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
      return value;
    };

    const metrics = [
        {
            title: "Total Calls",
            value: `${displayData.totalCalls?.totalCalls || 0}`,
            icon: Call,
            change: displayData.totalCalls?.percentageChange,
            inc: isIncrease(displayData.totalCalls?.percentageChange),
            subtitle:"Total call duration accumulated over the selected time period.",
            chart:(
                <ResponsiveContainer width="100%" height={400}>
                <AreaChart
                data={displayData.totalCalls?.dataPoints}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                <defs>
                    <linearGradient id="colorTotalCalls" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                    </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                    dataKey="date"
                    tickFormatter={formatDate}
                    interval="preserveStartEnd"
                    minTickGap={60}
                    angle={-45}
                    textAnchor="end"
                    height={70}
                    tick={{ fontSize: 12 }}
                >
                <Label value="Date" position="insideBottom" />
                </XAxis>
                <YAxis
                    tickFormatter={formatYAxis}
                    width={80}
                    tick={{ fontSize: 12 }}
                >
                <Label value="Total Calls" angle={-90} position="insideLeft" style={{ textAnchor: 'middle' }} />
                </YAxis>
                <Tooltip
                    labelFormatter={(label) => `Date: ${formatDate(label)}`}
                    formatter={(value) => [`${value.toLocaleString()} calls`, 'Total Calls']}
                    contentStyle={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}
                />
                        <Legend verticalAlign="top" height={36} width='100%' horizontalAlign="center" />
                        <Area
                    type="monotone"
                    dataKey="totalCalls"
                    stroke="#8884d8"
                    fillOpacity={1}
                    fill="url(#colorTotalCalls)"
                    name="Total Calls"
                />
                </AreaChart>
                </ResponsiveContainer>
            )       
        },
        {
            title: "Average Handle Time",
            value: `${displayData.avgCallMinutes?.avgDuration || 0} S`,
            icon: Timelapse,
            change: displayData.avgCallMinutes?.percentageChange,
            inc: isIncrease(displayData.avgCallMinutes?.percentageChange),
            chart:(
                <ResponsiveContainer width={"100%"} height={400}>
                <AreaChart
                data={displayData.avgCallMinutes?.dataPoints}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                > 
                <defs>
                    <linearGradient id="colorHandleTime" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                    </linearGradient>
                </defs>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="date"
                            tickFormatter={formatDate}
                            interval="preserveStartEnd"
                            minTickGap={60}
                            angle={-45}
                            textAnchor="end"
                            height={70}
                            tick={{ fontSize: 12 }}
                        >
                            <Label value="Date" position="insideBottom"/>
                        </XAxis>
                        <YAxis
                            tickFormatter={formatYAxis}
                            width={80}
                            tick={{ fontSize: 12 }}
                        >
                            <Label value="Average Call Minutes" angle={-90} position="insideLeft" style={{ textAnchor: 'middle' }} />
                        </YAxis>
                        <Tooltip
                            labelFormatter={(label) => `Date Range: ${formatDate(label)}`}
                            
                        />
                        <Tooltip
                            labelFormatter={(label) => `Date: ${formatDate(label)}`}
                            formatter={(value) => [`${formatDuration(value)}`, 'Average Handle Time']}
                            contentStyle={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}
                        />
                        <Legend verticalAlign="top" height={36} width='100%' horizontalAlign="center" />
                        <Area
                            type="monotone"
                            dataKey="avgDuration"
                            stroke="#8884d8"
                            fillOpacity={1}
                            activeDot={{ r: 4 }}
                            fill="url(#colorHandleTime)"
                            name="Average Handle Time"
                        />
                    </AreaChart>
                </ResponsiveContainer>
                )
        },
        {
            title: "Abandonment Rate",
            value: `${displayData?.abandonmentRate?.rate || 0}%`,
            icon: Error,
            change: displayData.abandonmentRate?.percentageChange,
            inc: isIncrease(displayData.abandonmentRate?.percentageChange),
            chart:(
                <ResponsiveContainer width="100%" height={400}>
                <AreaChart
                    data={displayData.abandonmentRate?.dataPoints}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                    >
                    <defs>
                        <linearGradient id="colorAbandonmentRate" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                        </linearGradient>
                    </defs> 
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="date"
                            tickFormatter={formatDate}
                            interval="preserveStartEnd"
                            minTickGap={60}
                            angle={-45}
                            textAnchor="end"
                            height={70}
                            tick={{ fontSize: 12 }}
                        >
                        <Label value="Date" position="insideBottom" />
                        </XAxis>
                        <YAxis
                            tickFormatter={formatYAxis}
                            width={80}
                            tick={{ fontSize: 12 }}
                        >
                            <Label value="Abandonment Rate" angle={-90} position="insideLeft" style={{ textAnchor: 'middle' }} />
                        </YAxis>
                        <Tooltip
                            labelFormatter={(label) => `Date: ${formatDate(label)}`}
                            formatter={(value) => [`${value.toLocaleString()}%`, 'Abandonment Rate']}
                            contentStyle={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}
                        />
                        <Legend verticalAlign="top" height={36} width='100%' horizontalAlign="center" />
                        <Area
                            type="monotone"
                            dataKey="abandonmentRate"
                            stroke="#8884d8"
                            fillOpacity={1}
                            fill="url(#colorAbandonmentRate)"
                            name="Abandonment Rate"
                        />
                    </AreaChart>
                </ResponsiveContainer>
            ),
        },
        
        {
            title:"Words Per Minute",
            value:`${displayData.wpmAnalysis?.avgWPM || 0}`,
            icon: Speed,
            change: displayData.wpmAnalysis?.percentageChange,
            inc: isIncrease(displayData.wpmAnalysis?.percentageChange),
            chart:(
                <ResponsiveContainer width="100%" height={400}>
                        <AreaChart 
                        data={displayData.wpmAnalysis?.dataPoints}
                        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                        <defs>
                            <linearGradient id="colorWordsPerMinutes" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                            </linearGradient>
                        </defs>
                        
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="date"
                                tickFormatter={formatDate}
                                interval="preserveStartEnd"
                                minTickGap={60}
                                angle={-45}
                                textAnchor="end"
                                height={70}
                                tick={{ fontSize: 12 }}
                            >
                            <Label value="Date" position="insideBottom" />
                            </XAxis>
                            <YAxis
                                tickFormatter={formatYAxis}
                                width={80}
                                tick={{ fontSize: 12 }}
                            >
                                <Label value="Words Per Minute" angle={-90} position="insideLeft" style={{ textAnchor: 'middle' }} />
                            </YAxis>
                            <Tooltip
                                labelFormatter={(label) => `Date: ${formatDate(label)}`}
                                formatter={(value) => [`${value.toLocaleString()} WPM`, 'Words per Minute']}
                                contentStyle={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}
                            />
                            <Area
                                type="monotone"
                                dataKey="wpm"
                                stroke="#8884d8"
                                fillOpacity={1}
                                fill="#A5A8E6"
                                name="Words Per Minute"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
            ),
        },
    ];
    
    return (
        <>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem"}}>
                <Box>
                </Box>

                <Grid container spacing={2}>
                    {metrics.map((metric, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <MetricCard {...metric} />
                        </Grid>
                    ))}
                </Grid>

                <Grid container spacing={2}>
                    <CallAnalysisGrid
                        displayData={displayData}
                        filter={filter}
                        assistantId={assistantId}
                        downloadCallAnalysisReport={downloadCallAnalysisReport}
                    />

                <Grid item xs={12} md={6}>
                        <ChartContainer
                            title="Words Per Minute Analysis"
                            subtitle="Detailed analysis of Speaking Rate over time"
                            download={() => downloadWPMMetricsCSV({ 
                                filter, 
                                assistant: assistantId, 
                                organization: null 
                            })}
                            chart={
                                hasWpmData ? (
                                    <AreaChart data={dataPoints}  margin={{ top: 10, right: 30, left: 0, bottom: 20 }}>
                                    <defs>
                                        <linearGradient id="colorWpm" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                                        <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="date"
                                            tickFormatter={formatDate}
                                            interval="preserveStartEnd"
                                            angle={0}
                                            minTickGap={25}
                                            textAnchor="end"
                                            height={50}
                                            fontSize={10}
                                            tick={{ fontSize: 12 }}
                                        >
                                            <Label
                                                value="Date"
                                                position="insideBottom"
                                                height="100"
                                            />
                                        </XAxis>
                                        <YAxis 
                                            tickFormatter={(value) => `${value} WPM`} 
                                            fontSize={10}
                                            width={80}
                                            tick={{ fontSize: 12 }}
                                        >
                                            <Label
                                                value="Words Per Minute"
                                                angle={-90}
                                                position="insideLeft"
                                                style={{ textAnchor: 'middle' }}
                                            />
                                        </YAxis>
                                        <Tooltip
                                            labelFormatter={(label) => `Date Range: ${formatDate(label)}`}
                                            formatter={(value) => [`${value.toFixed(2)} WPM`, 'Speaking Rate']}
                                            cursor={{ fill: "transparent" }}
                                            contentStyle={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="wpm"
                                            stroke="#8884d8"
                                            fill="#A5A8E6"
                                            fillOpacity={0.3}
                                            dot={false}
                                            activeDot={true}
                                        />
                                    </AreaChart>
                                ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                        <Typography variant="body1" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                        No data found for applied filters.
                                        </Typography>
                                    </Box>
                                )
                            }
                        />
                    </Grid>
                </Grid>

                <Grid container spacing={2}>
                <FirstCallResolutionGrid 
                    displayData={displayData}
                    assistantId={assistantId}
                    downloadFirstCallResolutionCSV={downloadFirstCallResolutionCSV}
                    loading={loading}
                    filter={filter}
                />
                <AIToHumanHandoverRateGrid 
                    displayData={displayData}
                    assistantId={assistantId}
                    downloadAIToHumanTransferCSV={downloadAIToHumanTransferCSV}
                    loading={loading}
                    filter={filter}
                />
                </Grid>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <ChartContainer
                            title={"Peak Hours Traffic"}
                            subtitle="Call volume trends by hour for optimal staffing."
                            download={() => downloadPeakHourTrafficCSV({ filter, assistant: assistantId, organization: null })}
                            chart={
                                displayData.peakHourTraffic ? (
                                    <ResponsiveContainer width="100%" height={400}>
                                        <AreaChart
                                        width={800}
                                        height={400}
                                        data={displayData.peakHourTraffic}
                                        >
                                        <defs>
                                        <linearGradient id="colorPeakHours" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                                        <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="hour" height={50} fontSize={10}>
                                            <Label value="Hour of the Day" position="insideBottom" />
                                        </XAxis>
                                        <YAxis fontSize={10}>
                                            <Label
                                            value="Number of Calls"
                                            angle={-90}
                                            position="insideLeft"
                                            style={{ textAnchor: "middle" }}
                                            />
                                        </YAxis>
                                        <Tooltip 
                                        content={({ payload }) => 
                                            <div style={{ 
                                            display: "flex", 
                                            flexDirection: "column", 
                                            backgroundColor: "white", 
                                            padding: ".5rem 1rem", 
                                            borderRadius: "5px", 
                                            outline: "1px solid #CCC" 
                                            }}>
                                            <span>Hour: {payload[0]?.payload.hour}</span>
                                            <span>Call Count: {payload[0]?.value}</span>
                                            </div>
                                        } 
                                        />
                                        <Area 
                                            type="monotone" 
                                            dataKey="callCount" 
                                            stroke="#A5A8E6" 
                                            fill="#A5A8E6"
                                            fillOpacity={0.3} 
                                        />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                    ) : (
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                            <Typography variant="body1" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                            No data found for applied filters.
                                            </Typography>
                                        </Box>
                                    )
                            }
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                    <ChartContainer
                    title={"Average Handle Time by Issue Type"}
                    subtitle="Average resolution time per issue type."
                    download={() => downloadAvgDurationByIssueTypeCSV({ filter, assistant: assistantId, organization: null })}
                    chart={
                        <AvgCallMinutesBarChart displayData={displayData} />
                    }
                />


                    </Grid>
                    </Grid >
            </Box>
        </>
    )
});

export default AssistantOverview;
